import { useRef, useState } from 'react';
import { useCMS } from '../../contexts/CMSContext';
import { compressImage } from '../../utils/imageCompression';
import { getEffectiveImageUrl, mobileImageKey } from '../../utils/cmsMobile';

interface EditableImageProps {
  /** Clave en CMS sin sufijo _mobile (p. ej. hero_photo). */
  imageKey: string;
  alt: string;
  className?: string;
  wrapperClassName?: string;
}

export default function EditableImage({
  imageKey,
  alt,
  className = '',
  wrapperClassName = '',
}: EditableImageProps) {
  const { isEditMode, images, updateImage, isMobileContentActive } = useCMS();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const baseKey = imageKey.replace(/_mobile$/, '');
  const storageKey = isMobileContentActive ? mobileImageKey(baseKey) : baseKey;
  const src = getEffectiveImageUrl(images, baseKey, isMobileContentActive, '');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona una imagen válida.');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const compressed = await compressImage(file);
      await updateImage(storageKey, compressed);
    } catch {
      setError('Error al procesar la imagen. Intenta de nuevo.');
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  if (!isEditMode) {
    return (
      <div className={wrapperClassName}>
        <img src={src} alt={alt} className={className} />
      </div>
    );
  }

  return (
    <div
      className={`${wrapperClassName} relative group cursor-pointer`}
      onClick={() => inputRef.current?.click()}
    >
      <img
        src={src}
        alt={alt}
        className={`${className} transition-opacity ${isUploading ? 'opacity-50' : 'group-hover:opacity-80'}`}
      />

      <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-xl">
        {isUploading ? (
          <div className="text-white text-center">
            <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
              <i className="ri-loader-4-line text-2xl animate-spin"></i>
            </div>
            <p className="text-sm font-medium">Subiendo...</p>
          </div>
        ) : (
          <div className="text-white text-center px-4">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-white/20 rounded-full">
              <i className="ri-image-edit-line text-xl"></i>
            </div>
            <p className="text-sm font-semibold">
              {isMobileContentActive ? 'Imagen (móvil)' : 'Cambiar imagen'}
            </p>
            <p className="text-xs opacity-80 mt-1">Haz clic para seleccionar</p>
          </div>
        )}
      </div>

      {error && (
        <div className="absolute bottom-2 left-2 right-2 bg-red-500/90 text-white text-xs px-2 py-1 rounded text-center">
          {error}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
